import React, { useContext, useEffect, useState } from 'react'

import { TimerStopwatch } from '@amzn/katal-metrics/lib/metricObject'
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Input, InputWrapper, Select } from '@amzn/stencil-react-components/form'
import { useAsync } from '@amzn/stencil-react-components/hooks'
import { Row, Spacer, View } from '@amzn/stencil-react-components/layout'
import { RichTextEditor } from '@amzn/stencil-react-components/rte'
import { Spinner } from '@amzn/stencil-react-components/spinner'
import { TableColumn } from '@amzn/stencil-react-components/table'
import { Text } from '@amzn/stencil-react-components/text'

import { MastheadContainer } from 'src/components/MastheadContainer'
import {
    CommonUserEventKeys,
    publishTimeSpentMetrics,
    publishUserEventMetrics,
    UserEventMethodNames,
} from 'src/metrics'
import { ItemPoolGenerator } from 'src/pages/module-builder/item-pool-editor/ItemPoolGenerator'
import { ItemPoolService } from 'src/services/item-pools/ItemPoolService'

export enum AllowedItemPoolType {
    RankingItem = 'Ranking Item',
    SlotPickerItem = 'Item Placement Item',
    MCQ = 'Multi Choice Item',
}

export interface PoolResults {
    versionId: string
    size: number
    type: string
}

const MIN_ITEM_POOL_SIZE = 50

const MAX_ITEM_POOL_SIZE = 10000

export function poolSizeError(poolSize: number) {
    return poolSize < MIN_ITEM_POOL_SIZE || poolSize > MAX_ITEM_POOL_SIZE
}

export type GetAdditionalTableCells = () => TableColumn<PoolResults, { versionId: string }>[]
export const ItemPoolGeneratorContext = React.createContext<{
    getAdditionalTableCells?: GetAdditionalTableCells
    itemPoolVersionId?: string
    isAutoGenerated?: boolean
}>({})

const pageTimer = new TimerStopwatch('ItemPoolEditorPageTimer')

export const ItemPoolEditor = () => {
    const { itemPoolVersionId } = useContext(ItemPoolGeneratorContext)
    const [itemType, setItemType] = useState<AllowedItemPoolType>(
        AllowedItemPoolType.SlotPickerItem
    )

    const [itemGenerationConfig, setItemGenerationConfig] = useState<string>('default')

    const [size, setSize] = useState<number>(MIN_ITEM_POOL_SIZE)

    const [downloadingConfig, setDownloadingConfig] = useState(false)

    const { data: isAutoGenerated } = useAsync(async () => {
        if (!itemPoolVersionId) return false
        return await ItemPoolService.checkIfItemPoolIsAutoGenerated(itemPoolVersionId)
    }, [itemPoolVersionId])

    const downloadConfig = async () => {
        try {
            setDownloadingConfig(true)

            const res = await ItemPoolService.exportItemPool(itemPoolVersionId as string)

            const element = document.createElement('a')
            element.id = `download-item-pool-config-${itemPoolVersionId as string}`
            element.href = URL.createObjectURL(res)
            element.ariaRoleDescription = 'link'
            element.download = `Item Pool Config - ${itemPoolVersionId as string}.zip`
            document.body.appendChild(element)
            element.click()
            document.body.removeChild(element)
        } catch (e) {
            console.error('Error downloading config:', e)
        } finally {
            setDownloadingConfig(false)
        }
    }

    useEffect(() => {
        pageTimer.start()
        publishUserEventMetrics(UserEventMethodNames.ItemPoolEditor, CommonUserEventKeys.Open)

        return () => {
            pageTimer.stop()
            publishTimeSpentMetrics(UserEventMethodNames.ItemPoolEditor, pageTimer.value)
        }
    }, [])

    return (
        <MastheadContainer>
            <View>
                {itemPoolVersionId && isAutoGenerated && (
                    <>
                        <Row gridGap='S300' alignItems='center'>
                            <Text>Item Pool Version Id: {itemPoolVersionId}</Text>
                            <Button
                                dataTestId='download-config-button'
                                onClick={downloadConfig}
                                variant={ButtonVariant.Secondary}
                                disabled={downloadingConfig}
                            >
                                {downloadingConfig ? <Spinner /> : 'Download'}
                            </Button>
                        </Row>
                        <Spacer height='S400' />
                    </>
                )}
                <Row gridGap='S300' alignItems={'flex-start'} flexWrap={'wrap'}>
                    <InputWrapper labelText='Select Item type' id='item-pool-item-type-select'>
                        {(props) => (
                            <Select
                                options={Object.values(AllowedItemPoolType)}
                                placeholder='Select one'
                                dataTestId={'item-pool-type'}
                                {...props}
                                value={itemType}
                                onChange={setItemType}
                            />
                        )}
                    </InputWrapper>
                    <InputWrapper
                        id='item-pool-size'
                        labelText='Item pool size'
                        tooltipText='Specify the item pool size. It can range between 50 and 10000'
                        required={true}
                        footer={undefined}
                    >
                        {(props) => (
                            <Input
                                type='number'
                                value={size}
                                dataTestId={'item-pool-generator-size'}
                                onChange={(e) => {
                                    setSize(parseInt(e.target.value))
                                }}
                                min={5}
                                max={MAX_ITEM_POOL_SIZE}
                                width='100%'
                                placeholder='Enter Item pool size'
                                {...props}
                            />
                        )}
                    </InputWrapper>
                </Row>
                <Spacer height='S400' />
                <Row gridGap='S300' alignItems={'flex-start'} flexWrap={'wrap'}>
                    <RichTextEditor
                        defaultContent='default'
                        defaultHeight={500}
                        hasWordCounter={true}
                        placeholder='Write something here'
                        recommendedWordCount={200}
                        onChange={(editor) => {
                            setItemGenerationConfig(editor.getText())
                        }}
                    />
                </Row>
                <Spacer height='S400' />
                <ItemPoolGenerator
                    itemType={itemType}
                    size={size}
                    itemGenerationInput={itemGenerationConfig}
                    hrefFunction={(r) => URL.createObjectURL(r)}
                    itemPoolVersionId={itemPoolVersionId}
                />
            </View>
        </MastheadContainer>
    )
}
